import { useQuery } from '@tanstack/react-query';
import { SEMI_FRESH } from '../../lib/queryPresets';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid } from 'lucide-react';
import { Card } from '../ui/Card';
import { WIDGET_HEADER_FLUSH, WidgetCardHeader } from './WidgetCardHeader';
import { Spinner } from '../ui/Spinner';
import { listFamilyStepRows } from '../../services/dossiers/friseService';
import { familyStepCounts } from '../../lib/dossiers/friseBoard';
import { useAuthStore } from '../../store/useAuthStore';
import { useIsAdminOrSecretary } from '../../hooks/usePermissions';

export function DossiersKanbanWidget() {
  const navigate = useNavigate();
  const collaborator = useAuthStore((s) => s.collaborator);
  const isAdminOrSecretary = useIsAdminOrSecretary();
  const leadCounselId = isAdminOrSecretary ? undefined : collaborator?.id;

  const { data: rows, isLoading } = useQuery({
    ...SEMI_FRESH,
    queryKey: ['dashboard', 'family-steps', leadCounselId ?? 'all'],
    queryFn: () => listFamilyStepRows(leadCounselId),
  });

  const groups = familyStepCounts(rows ?? []);

  return (
    <Card
      className="h-full"
      headerClassName={WIDGET_HEADER_FLUSH}
      header={
        <WidgetCardHeader
          icon={LayoutGrid}
          title={`Dossiers par famille${isAdminOrSecretary ? ' — cabinet' : ''}`}
          href="/dossiers/kanban"
        />
      }
    >
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : groups.length === 0 ? (
        <p className="text-xs text-neutral-400 italic">Aucun dossier ouvert.</p>
      ) : (
        <div className="space-y-3">
          {groups.map((group) => (
            <section key={group.familyName}>
              <h3 className="text-xs font-semibold text-neutral-700 mb-1">{group.familyName}</h3>
              <ul className="space-y-1.5">
                {group.lines.map((line) => (
                  <li key={`${line.friseId}:${line.position}`}>
                    <button
                      type="button"
                      onClick={() => navigate(`/dossiers/kanban?frise=${line.friseId}`)}
                      className="w-full flex items-baseline justify-between gap-3 text-left hover:text-cime-primary"
                    >
                      <span className="text-xs text-neutral-600 truncate">{line.text}</span>
                      <span className="text-sm font-bold text-neutral-900 tabular-nums">{line.count}</span>
                    </button>
                    {line.titles.length > 0 && (
                      <p className="text-[11px] text-neutral-400 truncate">{line.titles.join(' · ')}</p>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </Card>
  );
}
